/**
 * Field Tracking for Smart Select Optimization.
 *
 * Provides infrastructure for tracking which fields are accessed during
 * a select callback, enabling the query compiler to selectively project
 * only those fields instead of fetching the full props blob.
 */
import type { KindEntity } from "../../core/types";
import { EDGE_META_KEYS, NODE_META_KEYS } from "../../system-fields";
import { compareStrings } from "../../utils/compare";
import { createDataKeyedBag, isInteropProbeKey } from "../../utils/object";
import { mergeEdgeKinds, type SelectiveField, type ValueType } from "../ast";
import { type QueryBuilderState } from "../builder/types";
import {
  type FieldTypeInfo,
  type SchemaIntrospector,
} from "../schema-introspector";
import { SELECTABLE_ALIAS_MARKER } from "./selectable-alias";

// ============================================================
// Types
// ============================================================

type TrackingValueMode = "falsy" | "truthy" | "max";

type AccessedField = Readonly<{
  alias: string;
  field: string;
  isSystemField: boolean;
}>;

export type TrackingContextOptions = Readonly<{
  schemaIntrospector: SchemaIntrospector;
  mode: TrackingValueMode;
  /**
   * When "absent", optional traversal aliases are set to undefined to
   * encourage exploring fallback branches (e.g., `ctx.friend ? ... : ...`).
   */
  optionalTraversalAliases: "present" | "absent";
}>;

// ============================================================
// Constants
// ============================================================

/**
 * Names the tracking proxies answer with the inherited `Object.prototype`
 * member, so a select callback that stringifies or type-tests an alias object
 * during tracking behaves like it would against a real result object.
 *
 * A name on this list is a prototype member only while the schema does NOT
 * declare a field by that name — `z.object({ toString: z.string() })` is an
 * ordinary schema, and its field is ordinary data. Membership is therefore
 * decided by {@link SchemaIntrospector.hasDeclaredField}, the same own-key
 * question `hasOwnKey` answers everywhere else; a declared name never reaches
 * this list. Classifying it here instead left the field untracked, so the
 * selective projection never selected it and the guarded result proxy served
 * the inherited member in place of the stored value.
 */
const OBJECT_PROTOTYPE_PROPERTIES = new Set<string>([
  "__proto__",
  "constructor",
  "hasOwnProperty",
  "isPrototypeOf",
  "propertyIsEnumerable",
  "toLocaleString",
  "toString",
  "valueOf",
]);

// ============================================================
// FieldAccessTracker
// ============================================================

export class FieldAccessTracker {
  requiresFullRow = false;

  readonly #fields = new Map<string, AccessedField>();

  record(alias: string, field: string, isSystemField: boolean): void {
    const key = `${alias}\u0000${field}`;
    const existing = this.#fields.get(key);
    // System fields (id, kind) are dedicated columns — not in the props JSON.
    // Never downgrade a system field to a props field, as that would cause the
    // compiler to emit props->>'id' (nonexistent) instead of the id column.
    if (existing !== undefined && existing.isSystemField && !isSystemField) {
      return;
    }
    this.#fields.set(key, { alias, field, isSystemField });
  }

  getAccessedFields(): readonly AccessedField[] {
    return [...this.#fields.values()];
  }
}

// ============================================================
// Tracking Context Creation
// ============================================================

export function createTrackingContext(
  state: QueryBuilderState,
  tracker: FieldAccessTracker,
  options: TrackingContextOptions,
): Record<string, unknown> {
  // Data-keyed: alias names chosen by the caller's query. The tracking context
  // must be keyed identically to the real one `buildSelectContext` produces —
  // a tracking pass that drops an alias the execution pass keeps (or the
  // reverse) is exactly the probe-vs-engine divergence to avoid.
  const context = createDataKeyedBag<unknown>();
  context[state.startAlias] = createNodeTrackingProxy(
    state.startAlias,
    state.startKinds,
    tracker,
    options,
  );

  for (const traversal of state.traversals) {
    const edgeKindNames = mergeEdgeKinds(traversal);

    const optionalAbsent =
      options.optionalTraversalAliases === "absent" && traversal.optional;

    context[traversal.nodeAlias] =
      optionalAbsent ? undefined : (
        createNodeTrackingProxy(
          traversal.nodeAlias,
          traversal.nodeKinds,
          tracker,
          options,
        )
      );

    context[traversal.edgeAlias] =
      optionalAbsent ? undefined : (
        createEdgeTrackingProxy(
          traversal.edgeAlias,
          edgeKindNames,
          tracker,
          options,
        )
      );

    // Provide placeholder values for recursive depth/path so the
    // select callback can access them without crashing during tracking.
    const vl = traversal.variableLength;
    if (vl !== undefined) {
      if (vl.depthAlias !== undefined) {
        context[vl.depthAlias] = options.mode === "falsy" ? 0 : 1;
      }
      if (vl.pathAlias !== undefined) {
        context[vl.pathAlias] =
          options.mode === "falsy" ? []
          : vl.pathFormat === "qualified" ?
            [{ type: "node", kind: "placeholder", id: "placeholder" }]
          : ["placeholder"];
      }
    }
  }

  // Spread at the boundary, for the same reason `buildSelectContext` does: this
  // context is handed to the CALLER's `select` callback, on the tracking pass as
  // much as on the execution pass. A callback that asks `ctx instanceof Object`
  // must get the same answer from both, or the probe explores a branch the
  // engine will not take.
  return { ...context };
}

function createNodeTrackingProxy(
  alias: string,
  kindNames: readonly string[],
  tracker: FieldAccessTracker,
  options: TrackingContextOptions,
): unknown {
  return new Proxy(
    { [SELECTABLE_ALIAS_MARKER]: true },
    {
      ownKeys: () => {
        tracker.requiresFullRow = true;
        return [];
      },
      get: (_, property: string | symbol) => {
        if (typeof property === "symbol") return;
        // The interop probes are exempted so an incidental `await` or
        // `JSON.stringify` of the tracking context does not record a phantom
        // field. Neither is on `Object.prototype`, but both earn the same rule
        // as the members that are: a DECLARED field named `then` or `toJSON` is
        // stored data, and exempting it would leave it untracked — the
        // selective projection would omit the column the select callback just
        // read, and the result mapper would have no own key to hand back.
        if (
          isInteropProbeKey(property) &&
          !options.schemaIntrospector.hasDeclaredField(kindNames, property)
        ) {
          return;
        }

        if (
          OBJECT_PROTOTYPE_PROPERTIES.has(property) &&
          !options.schemaIntrospector.hasDeclaredField(kindNames, property)
        ) {
          if (property === "constructor") return Object;
          if (property === "__proto__") return Object.prototype;
          return Reflect.get(Object.prototype, property) as unknown;
        }

        if (property === "id" || property === "kind") {
          tracker.record(alias, property, true);
          return getPlaceholderForSystemField(property, options.mode);
        }

        if (property === "meta") {
          for (const key of NODE_META_KEYS) {
            tracker.record(alias, `meta.${key}`, true);
          }
          return buildNodeMetaPlaceholder(options.mode);
        }

        tracker.record(alias, property, false);
        const typeInfo = options.schemaIntrospector.getSharedFieldTypeInfo(
          kindNames,
          property,
        );
        return getPlaceholderForTypeInfo(typeInfo, options.mode);
      },
    },
  );
}

function createEdgeTrackingProxy(
  alias: string,
  edgeKindNames: readonly string[],
  tracker: FieldAccessTracker,
  options: TrackingContextOptions,
): unknown {
  return new Proxy(
    { [SELECTABLE_ALIAS_MARKER]: true },
    {
      ownKeys: () => {
        tracker.requiresFullRow = true;
        return [];
      },
      get: (_, property: string | symbol) => {
        if (typeof property === "symbol") return;
        // See `createNodeTrackingProxy`: a DECLARED `then` / `toJSON` is stored
        // data and must be tracked, not exempted.
        if (
          isInteropProbeKey(property) &&
          !options.schemaIntrospector.hasDeclaredEdgeField(
            edgeKindNames,
            property,
          )
        ) {
          return;
        }

        if (
          OBJECT_PROTOTYPE_PROPERTIES.has(property) &&
          !options.schemaIntrospector.hasDeclaredEdgeField(
            edgeKindNames,
            property,
          )
        ) {
          if (property === "constructor") return Object;
          if (property === "__proto__") return Object.prototype;
          return Reflect.get(Object.prototype, property) as unknown;
        }

        if (
          property === "id" ||
          property === "kind" ||
          property === "fromId" ||
          property === "toId"
        ) {
          tracker.record(alias, property, true);
          return getPlaceholderForSystemField(property, options.mode);
        }

        if (property === "meta") {
          for (const key of EDGE_META_KEYS) {
            tracker.record(alias, `meta.${key}`, true);
          }
          return buildEdgeMetaPlaceholder(options.mode);
        }

        tracker.record(alias, property, false);
        const typeInfo = options.schemaIntrospector.getSharedEdgeFieldTypeInfo(
          edgeKindNames,
          property,
        );
        return getPlaceholderForTypeInfo(typeInfo, options.mode);
      },
    },
  );
}

// ============================================================
// Selective Field Construction
// ============================================================

type BuildSelectiveFieldsOptions = Readonly<{
  state: QueryBuilderState;
  schemaIntrospector: SchemaIntrospector;
}>;

export function buildSelectiveFields(
  accessedFields: readonly AccessedField[],
  options?: BuildSelectiveFieldsOptions,
): readonly SelectiveField[] {
  const aliasInfo = options ? buildAliasKindMap(options.state) : undefined;

  return accessedFields
    .map((access) => {
      const base: SelectiveField = {
        alias: access.alias,
        field: access.field,
        outputName: `${access.alias}_${access.field}`,
        isSystemField: access.isSystemField,
      };

      if (!options || access.isSystemField) {
        return base;
      }

      const info = aliasInfo?.get(access.alias);
      if (!info) {
        return base;
      }

      const typeInfo =
        info.kind === "node" ?
          options.schemaIntrospector.getSharedFieldTypeInfo(
            info.kindNames,
            access.field,
          )
        : options.schemaIntrospector.getSharedEdgeFieldTypeInfo(
            info.kindNames,
            access.field,
          );

      return {
        ...base,
        valueType: typeInfo?.valueType,
      };
    })
    .toSorted((a, b) => {
      const aliasCompare = compareStrings(a.alias, b.alias);
      if (aliasCompare !== 0) return aliasCompare;
      return compareStrings(a.field, b.field);
    });
}

type AliasKind = KindEntity;

type AliasKindInfo = Readonly<{
  kind: AliasKind;
  kindNames: readonly string[];
}>;

function buildAliasKindMap(
  state: QueryBuilderState,
): ReadonlyMap<string, AliasKindInfo> {
  const map = new Map<string, AliasKindInfo>([
    [
      state.startAlias,
      {
        kind: "node",
        kindNames: state.startKinds,
      },
    ],
  ]);

  for (const traversal of state.traversals) {
    const edgeKindNames = mergeEdgeKinds(traversal);

    map.set(traversal.nodeAlias, {
      kind: "node",
      kindNames: traversal.nodeKinds,
    });
    map.set(traversal.edgeAlias, {
      kind: "edge",
      kindNames: edgeKindNames,
    });
  }

  return map;
}

// ============================================================
// Placeholder Values
// ============================================================

function getPlaceholderForSystemField(
  field: string,
  mode: TrackingValueMode,
): unknown {
  if (
    field === "id" ||
    field === "kind" ||
    field === "fromId" ||
    field === "toId"
  ) {
    return mode === "falsy" ? "" : "x";
  }
  return undefined;
}

function buildNodeMetaPlaceholder(mode: TrackingValueMode): Readonly<{
  version: number;
  validFrom: string | undefined;
  validTo: string | undefined;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | undefined;
}> {
  const empty = mode === "falsy";
  return {
    version: empty ? 0 : 1,
    validFrom: empty ? undefined : "2020-01-01T00:00:00.000Z",
    validTo: undefined,
    createdAt: "2020-01-01T00:00:00.000Z",
    updatedAt: "2020-01-01T00:00:00.000Z",
    deletedAt: undefined,
  };
}

function buildEdgeMetaPlaceholder(mode: TrackingValueMode): Readonly<{
  validFrom: string | undefined;
  validTo: string | undefined;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | undefined;
}> {
  const empty = mode === "falsy";
  return {
    validFrom: empty ? undefined : "2020-01-01T00:00:00.000Z",
    validTo: undefined,
    createdAt: "2020-01-01T00:00:00.000Z",
    updatedAt: "2020-01-01T00:00:00.000Z",
    deletedAt: undefined,
  };
}

function getPlaceholderForTypeInfo(
  typeInfo: FieldTypeInfo | undefined,
  mode: TrackingValueMode,
): unknown {
  if (!typeInfo) {
    return undefined;
  }

  return getPlaceholderForValueType(typeInfo.valueType, mode);
}

function getPlaceholderForValueType(
  valueType: ValueType,
  mode: TrackingValueMode,
): unknown {
  switch (valueType) {
    case "string":
    case "date": {
      if (mode === "falsy") return "";
      if (mode === "max") return "active";
      return "x";
    }
    case "number": {
      if (mode === "falsy") return 0;
      if (mode === "max") return 100;
      return 1;
    }
    case "boolean": {
      return mode !== "falsy";
    }
    case "array":
    case "embedding": {
      return [];
    }
    case "object": {
      return {};
    }
    case "unknown": {
      return undefined;
    }
  }
}
